import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DisplayLocationBar } from '../display'
import { listBookmarks, updateBookmark } from '../../services/bookmarks'
import { listNotebooks, updateNotebook } from '../../services/notebooks'
import { listSearchrooms, updateSearchroom } from '../../services/searchrooms'
import { listChatrooms, updateChatroom } from '../../services/chatrooms'
import { listGuides, updateGuide } from '../../services/guides'
import { listGlobalChats, updateGlobalChat } from '../../services/globalChats'
import { useModal } from '../../context/ModalContext'
import { IconBookmarkPlus } from '../../layout/NavRail/NavRail'
import { openItemInCode } from '../code/openInCode'
import { standardName } from '../../utils/standardRef'
import { openGuideInGuides } from '../guides/openInGuides'
import { notebookLabel } from '../notebooks/notebookLabel'
import { globalChatLabel } from '../globalsearch/globalChatLabel'
import { gridReducer, createInitialGrid } from '../grid/gridReducer'
import { loadGrid, saveGrid } from '../grid/gridStorage'
import './TopicWindowBody.css'

const LIMITS = { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 }

const KINDS = [
  { value: 'bookmarks', label: 'Bookmarks', empty: 'No bookmarks in this topic.' },
  { value: 'notebooks', label: 'Notebooks', empty: 'No notebooks in this topic.' },
  { value: 'searchrooms', label: 'Searchrooms', empty: 'No searchrooms in this topic.' },
  { value: 'chats', label: 'Chats', empty: 'No chats in this topic.' },
  { value: 'guides', label: 'Guides', empty: 'No guides in this topic.' },
  { value: 'global', label: 'Global AI Search', empty: 'No global searches in this topic.' },
]

const KIND = Object.fromEntries(KINDS.map((kind) => [kind.value, kind]))

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconArrow = ({ dir }) => (
  <svg {...svgProps}>
    {dir === 'left' ? <polyline points="15 6 9 12 15 18" /> : <polyline points="9 6 15 12 9 18" />}
  </svg>
)

const IconCaret = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

function KindSelect({ value, onChange }) {
  const label = KIND[value]?.label || 'Bookmarks'
  return (
    <label className="topic-kind-select">
      <span>{label}</span>
      <IconCaret />
      <select aria-label="What to list" value={value} onChange={(event) => onChange?.(event.target.value)}>
        {KINDS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  )
}

const IconCopy = () => (
  <svg {...svgProps}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

const IconMove = () => (
  <svg {...svgProps}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <polyline points="12 11 12 17" />
    <polyline points="9 14 12 17 15 14" />
  </svg>
)

const IconJump = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)

const IconLocate = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="7" />
    <line x1="12" y1="1" x2="12" y2="4" />
    <line x1="12" y1="20" x2="12" y2="23" />
    <line x1="1" y1="12" x2="4" y2="12" />
    <line x1="20" y1="12" x2="23" y2="12" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </svg>
)

function scopeIds(topics, topicId) {
  const key = String(topicId)
  const ids = [key]
  ;(topics || []).forEach((topic) => {
    if (!topic?._id) return
    const path = topic.path || []
    if (path.some((id) => topicKey(id) === key)) ids.push(String(topic._id))
  })
  return ids
}

function topicKey(value) {
  if (value && typeof value === 'object') return String(value._id || '')
  return String(value || '')
}

function hasAnyTopic(entity, ids) {
  const want = new Set(ids)
  return (entity?.topics || []).some((topic) => want.has(topicKey(topic)))
}

function filedIds(entity) {
  return (entity?.topics || []).map(topicKey).filter(Boolean)
}

function nextTopicIds(current, scope, destinationId, mode) {
  const dest = String(destinationId)
  const base = mode === 'move' ? current.filter((id) => !scope.has(id)) : [...current]
  if (!base.includes(dest)) base.push(dest)
  return base
}

function sameIds(a, b) {
  if (a.length !== b.length) return false
  const want = new Set(b)
  return a.every((id) => want.has(id))
}

function saveTopics(kind, id, topicIds) {
  const payload = { topics: topicIds }
  if (kind === 'bookmarks') return updateBookmark(id, payload)
  if (kind === 'notebooks') return updateNotebook(id, payload)
  if (kind === 'searchrooms') return updateSearchroom(id, payload)
  if (kind === 'chats') return updateChatroom(id, payload)
  if (kind === 'guides') return updateGuide(id, payload)
  return updateGlobalChat(id, payload)
}

function pageLabel(item) {
  if (!item || item.pageStart == null) return ''
  return `p.${item.pageStart}`
}

function openInGrid(storageKey, entityType, entityId) {
  if (!entityId || typeof window === 'undefined') return false
  let state = loadGrid(storageKey)
  const count = state.columns.reduce((n, col) => n + col.windows.length, 0)
  if (!count) state = createInitialGrid(entityType)
  state = gridReducer(state, {
    type: 'SELECT_ENTITY',
    entityType,
    entityId,
    limits: LIMITS,
  })
  saveGrid(storageKey, state)
  return true
}

async function loadRows(kind, topicId, nested, topics) {
  if (kind === 'bookmarks') {
    const bookmarks = await listBookmarks({ topic: topicId, nested })
    return bookmarks.map((bookmark) => {
      const item = bookmark.item
      return {
        id: bookmark._id,
        label: item?.label || 'Item',
        meta: [standardName(item?.standard), pageLabel(item)].filter(Boolean).join(' · '),
        where: 'Code',
        topicIds: filedIds(bookmark),
        open: () => (item && openItemInCode(item) ? '/code' : ''),
      }
    })
  }
  if (kind === 'notebooks') {
    const pages = await listNotebooks()
    const ids = nested ? scopeIds(topics, topicId) : [String(topicId)]
    return pages.filter((page) => hasAnyTopic(page, ids)).map((page) => ({
      id: page._id,
      label: notebookLabel(page),
      meta: '',
      where: 'Notes',
      topicIds: filedIds(page),
      open: () => (openInGrid('ct-grid-notebooks-v1', 'notebook', page._id) ? '/notebooks' : ''),
    }))
  }
  if (kind === 'searchrooms') {
    const data = await listSearchrooms({ facets: { topics: [topicId] }, limit: 100, nested })
    return (data.searchrooms || []).map((room) => ({
      id: room._id,
      label: room.label || 'Searchroom',
      meta: '',
      where: 'Searchrooms',
      topicIds: filedIds(room),
      open: () => (openInGrid('ct-grid-search-v1', 'searchroom', room._id) ? '/searchrooms' : ''),
    }))
  }
  if (kind === 'chats') {
    const rooms = await listChatrooms({ topics: [topicId], nested })
    return rooms.map((room) => ({
      id: room._id,
      label: room.displayLabel || room.label || 'Chat',
      meta: '',
      where: 'Chat',
      topicIds: filedIds(room),
      open: () => (openInGrid('ct-grid-chat-v1', 'chat', room._id) ? '/chat' : ''),
    }))
  }
  if (kind === 'guides') {
    const guides = await listGuides({ topics: [topicId], nested })
    return guides.map((guide) => ({
      id: guide._id,
      label: guide.title || guide.name || 'Guide',
      meta: '',
      where: 'Guides',
      topicIds: filedIds(guide),
      open: () => (openGuideInGuides(guide._id) ? '/guides' : ''),
    }))
  }
  const chats = await listGlobalChats({ topics: [topicId], limit: 100, nested })
  return chats.map((chat) => ({
    id: chat._id,
    label: globalChatLabel(chat),
    meta: '',
    where: 'Global AI Search',
    topicIds: filedIds(chat),
    open: () => (openInGrid('ct-grid-globalsearch-v1', 'globalchat', chat._id) ? '/global-ai-search' : ''),
  }))
}

// Everything filed under one topic. The bar under the window header switches
// the list between bookmarks, notebooks, searchrooms, chats, guides, and
// global searches that carry this topic.
function TopicWindowBody({
  topicId,
  topics,
  history,
  historyIndex,
  backTitle,
  forwardTitle,
  onHistory,
  onLocate,
  compact = false,
}) {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [kind, setKind] = useState('bookmarks')
  const [nested, setNested] = useState(false)
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('idle')
  const [selected, setSelected] = useState(() => new Set())
  const [listNonce, setListNonce] = useState(0)

  useEffect(() => {
    if (!topicId) {
      setRows([])
      setStatus('idle')
      return undefined
    }
    let alive = true
    setStatus('loading')
    loadRows(kind, topicId, nested, topics)
      .then((list) => {
        if (!alive) return
        setRows(list)
        setStatus('ready')
      })
      .catch(() => {
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [topicId, kind, nested, topics, listNonce])

  useEffect(() => {
    setSelected(new Set())
  }, [topicId, kind, nested])

  const addBookmark = () => {
    const topic = (topics || []).find((entry) => String(entry._id) === String(topicId))
    openModal('create-bookmark', {
      topics: topic ? [{ _id: topic._id, name: topic.name }] : [],
      onSaved: () => setListNonce((n) => n + 1),
    })
  }

  const refile = (mode) => {
    const chosen = rows.filter((row) => selected.has(String(row.id)))
    if (!chosen.length) return
    const scope = new Set(nested ? scopeIds(topics, topicId) : [String(topicId)])
    openModal('refile-topic', {
      mode,
      topics: topics || [],
      count: chosen.length,
      nested,
      onConfirm: async (destinationId) => {
        await Promise.all(
          chosen.map((row) => {
            const current = row.topicIds || []
            const next = nextTopicIds(current, scope, destinationId, mode)
            if (sameIds(next, current)) return null
            return saveTopics(kind, row.id, next)
          })
        )
        setSelected(new Set())
        setListNonce((n) => n + 1)
      },
    })
  }

  if (!topicId) return <div className="display-empty">Select a topic.</div>

  const empty = KIND[kind]?.empty || 'Nothing in this topic.'
  const index = Number.isInteger(historyIndex) ? historyIndex : -1
  const canBack = index > 0
  const canForward = Array.isArray(history) && index >= 0 && index < history.length - 1

  return (
    <div className={'topic-window' + (compact ? ' is-compact' : '')}>
      <div className="topic-kind-bar">
        <DisplayLocationBar>
          <div className="topic-kind-tools">
            <KindSelect value={kind} onChange={setKind} />
            <label className="topic-kind-nested" title="Also show items filed under topics inside this one">
              <input
                type="checkbox"
                checked={nested}
                onChange={(event) => setNested(event.target.checked)}
              />
              Include nested
            </label>
          </div>
          <div className="display-header-actions topic-kind-actions">
            {kind === 'bookmarks' ? (
              <button
                type="button"
                title="Bookmark an item in this topic"
                aria-label="Bookmark an item in this topic"
                onClick={addBookmark}
              >
                <IconBookmarkPlus />
              </button>
            ) : null}
            <button
              type="button"
              title={selected.size ? 'Copy to another topic' : 'Select items to copy'}
              aria-label="Copy to another topic"
              disabled={!selected.size}
              onClick={() => refile('copy')}
            >
              <IconCopy />
            </button>
            <button
              type="button"
              title={selected.size ? 'Move to another topic' : 'Select items to move'}
              aria-label="Move to another topic"
              disabled={!selected.size}
              onClick={() => refile('move')}
            >
              <IconMove />
            </button>
            {!compact && (
              <>
                <button
                  type="button"
                  title={backTitle || 'Back'}
                  aria-label={backTitle || 'Back'}
                  disabled={!canBack}
                  onClick={() => onHistory?.(-1)}
                >
                  <IconArrow dir="left" />
                </button>
                <button
                  type="button"
                  title={forwardTitle || 'Forward'}
                  aria-label={forwardTitle || 'Forward'}
                  disabled={!canForward}
                  onClick={() => onHistory?.(1)}
                >
                  <IconArrow dir="right" />
                </button>
                <button
                  type="button"
                  title="Find this topic in the sidebar"
                  aria-label="Find this topic in the sidebar"
                  onClick={() => onLocate?.()}
                >
                  <IconLocate />
                </button>
              </>
            )}
          </div>
        </DisplayLocationBar>
      </div>
      <div className="topic-window-list">
        {status === 'loading' ? (
          <div className="display-empty">Loading…</div>
        ) : status === 'error' ? (
          <div className="display-empty">Could not load this list.</div>
        ) : !rows.length ? (
          <div className="display-empty">{empty}</div>
        ) : (
          rows.map((row) => {
            const jump = () => {
              const path = row.open()
              if (path) navigate(path)
            }
            const jumpLabel = row.where ? `Open in ${row.where}` : 'Open'
            return (
              <div key={row.id} className="topic-bm" onClick={jump}>
                <input
                  type="checkbox"
                  className="topic-bm-check"
                  checked={selected.has(String(row.id))}
                  aria-label={`Select ${row.label}`}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => {
                    const id = String(row.id)
                    setSelected((prev) => {
                      const next = new Set(prev)
                      if (next.has(id)) next.delete(id)
                      else next.add(id)
                      return next
                    })
                  }}
                />
                <span className="topic-bm-label">{row.label}</span>
                <span className="topic-bm-meta">{row.meta}</span>
                <button
                  type="button"
                  className="topic-bm-jump"
                  title={jumpLabel}
                  aria-label={jumpLabel}
                  onClick={(event) => {
                    event.stopPropagation()
                    jump()
                  }}
                >
                  <IconJump />
                </button>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default TopicWindowBody
